import * as THREE from 'three';
import { EFFECT_LAYER } from '../enhanced-effects';
import { SUN_DIR } from './sky-dome';

// ---------------------------------------------------------------------------
// 5. Ground Fog — low-lying wispy fog with dual layers and zone-aware density
// ---------------------------------------------------------------------------

export function createGroundFog(scene: THREE.Scene): {
  mesh: THREE.Mesh;
  mesh2: THREE.Mesh;
  update: (dt: number, camPos: THREE.Vector3) => void;
  dispose: () => void;
} {
  // Shared fog shader factory
  function makeFogMaterial(baseAlpha: number, speed1X: number, speed1Z: number, speed2X: number, speed2Z: number, uvScale: number) {
    const uniforms = {
      uTime: { value: 0.0 },
      uCamPos: { value: new THREE.Vector3() },
      uBaseAlpha: { value: baseAlpha },
      uSunDir: { value: SUN_DIR.clone() },
    };

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      side: THREE.DoubleSide,
      uniforms,
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vWorldPos;
        void main() {
          vUv = uv;
          vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uCamPos;
        uniform float uBaseAlpha;
        uniform vec3 uSunDir;
        varying vec2 vUv;
        varying vec3 vWorldPos;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }

        float fbm(vec2 p) {
          float v = 0.0;
          float a = 0.5;
          for (int i = 0; i < 4; i++) {
            v += a * noise(p);
            p *= 2.0;
            a *= 0.5;
          }
          return v;
        }

        void main() {
          // World-space UVs for consistent fog regardless of camera
          vec2 worldUV = vWorldPos.xz * ${uvScale.toFixed(4)};

          // Two FBM layers moving in different directions
          float fog1 = fbm(worldUV + vec2(uTime * ${speed1X.toFixed(4)}, uTime * ${speed1Z.toFixed(4)}));
          float fog2 = fbm(worldUV * 1.5 + vec2(uTime * ${speed2X.toFixed(4)}, uTime * ${speed2Z.toFixed(4)}));
          float fog = fog1 * 0.6 + fog2 * 0.4;

          // Thin/wispy: threshold to create gaps
          fog = smoothstep(0.32, 0.62, fog);

          // Distance fade: close = transparent, medium = visible, far = fade out
          float dist = length(vWorldPos.xz - uCamPos.xz);
          float distFade = smoothstep(3.0, 12.0, dist) * smoothstep(100.0, 50.0, dist);

          // Zone-aware density: tunnels/corridors get thicker fog
          // B Tunnels (x:+22..+52, z:+10..+45) — boost density
          float bTunnels = smoothstep(18.0, 25.0, vWorldPos.x) * smoothstep(55.0, 48.0, vWorldPos.x)
                         * smoothstep(5.0, 12.0, vWorldPos.z) * smoothstep(50.0, 42.0, vWorldPos.z);
          // Mid corridor (x:-8..+18, z:-10..+30) — moderate boost
          float midCorr = smoothstep(-12.0, -5.0, vWorldPos.x) * smoothstep(22.0, 15.0, vWorldPos.x)
                        * smoothstep(-15.0, -8.0, vWorldPos.z) * smoothstep(35.0, 28.0, vWorldPos.z);
          // Long A (x:-42..-62, z:+5..+50) — corridor boost
          float longA = smoothstep(-65.0, -58.0, vWorldPos.x) * smoothstep(-38.0, -44.0, vWorldPos.x)
                      * smoothstep(0.0, 8.0, vWorldPos.z) * smoothstep(55.0, 48.0, vWorldPos.z);

          float zoneDensity = 1.0 + bTunnels * 0.6 + midCorr * 0.35 + longA * 0.5;

          float alpha = fog * uBaseAlpha * distFade * zoneDensity;

          // Color shifts: warmer fog in sunlit areas, cooler in shadows
          vec3 warmFog = vec3(0.90, 0.83, 0.65);
          vec3 coolFog = vec3(0.75, 0.76, 0.72);
          // Approximate sun exposure via world position (sun comes from +x, +z direction)
          float sunExposure = dot(normalize(vWorldPos.xz + 0.001), normalize(uSunDir.xz));
          sunExposure = sunExposure * 0.5 + 0.5; // remap to 0..1
          vec3 color = mix(coolFog, warmFog, sunExposure);

          gl_FragColor = vec4(color, alpha);
        }
      `,
    });

    return { mat, uniforms };
  }

  // Layer 1: low ground fog (y=0.2)
  const geo1 = new THREE.PlaneGeometry(200, 200, 1, 1);
  geo1.rotateX(-Math.PI / 2);
  const { mat: mat1, uniforms: uniforms1 } = makeFogMaterial(0.20, 0.03, 0.01, -0.02, 0.015, 0.02);
  const mesh1 = new THREE.Mesh(geo1, mat1);
  mesh1.userData.isEffect = true;
  mesh1.layers.set(EFFECT_LAYER);
  mesh1.position.y = 0.2;
  mesh1.renderOrder = 95;
  scene.add(mesh1);

  // Layer 2: higher wispy fog (y=1.0) — thinner, different speed
  const geo2 = new THREE.PlaneGeometry(200, 200, 1, 1);
  geo2.rotateX(-Math.PI / 2);
  const { mat: mat2, uniforms: uniforms2 } = makeFogMaterial(0.10, 0.04, -0.008, 0.015, 0.025, 0.018);
  const mesh2 = new THREE.Mesh(geo2, mat2);
  mesh2.userData.isEffect = true;
  mesh2.layers.set(EFFECT_LAYER);
  mesh2.position.y = 1.0;
  mesh2.renderOrder = 94;
  scene.add(mesh2);

  return {
    mesh: mesh1,
    mesh2: mesh2,
    update(dt: number, camPos: THREE.Vector3) {
      uniforms1.uTime.value += dt;
      uniforms1.uCamPos.value.copy(camPos);
      mesh1.position.x = camPos.x;
      mesh1.position.z = camPos.z;

      uniforms2.uTime.value += dt;
      uniforms2.uCamPos.value.copy(camPos);
      mesh2.position.x = camPos.x;
      mesh2.position.z = camPos.z;
    },
    dispose() {
      scene.remove(mesh1);
      geo1.dispose();
      mat1.dispose();
      scene.remove(mesh2);
      geo2.dispose();
      mat2.dispose();
    },
  };
}
